import {
  AlertTriangle,
  BarChart3,
  CheckCircle,
  ClipboardCheck,
  Clock,
  FileText,
  Gauge,
  PoundSterling,
  SearchCheck,
  ShieldCheck,
  UploadCloud,
  Users,
} from 'lucide-react';
import { getAppLoginUrl, getFleetTrialSignupUrl } from '../config/urls';
import { Navbar } from '../components/waitlist/Navbar';
import { Footer } from '../components/waitlist/Footer';
import { usePageMeta } from '../hooks/usePageMeta';
import { Analytics } from '../services/analytics';
import fleetHeroImage from '../../assets/fleet-operations-workspace.webp';

const FLEET_FAQS = [
  {
    q: 'Who is the Free 3-PCN FleetMode Check for?',
    a: 'It is for PCO hire companies that receive PCNs on company-registered vehicles and need to match each notice to the right driver, protect deadlines, and track recovery status.',
  },
  {
    q: 'Do we need to upload all our PCNs to try FleetMode?',
    a: 'No. Start with 3 PCNs. The trial is designed to show value from a small sample before your team considers a wider rollout.',
  },
  {
    q: 'Is FleetMode only for PCO hire companies?',
    a: 'PCO hire is the current campaign focus. FleetMode also includes an owner-managed fleet workflow for operators who own or manage vehicles and need to track, appeal, pay, or recharge PCNs internally.',
  },
  {
    q: 'Does FleetMode replace our admin team?',
    a: 'No. FleetMode helps your admin team process PCNs faster, prepare evidence, spot risks, and keep a clearer record. Your team stays in control of decisions.',
  },
  {
    q: 'Does the AI make legal decisions or guarantee transfer success?',
    a: 'No. AI helps extract data, prepare summaries, and flag readiness. Every output is for review and does not guarantee liability transfer, appeal success, or a legal outcome.',
  },
  {
    q: 'What happens after the first 3 PCNs?',
    a: 'FleetMode creates a 3-PCN AI Trial Snapshot. If the sample looks useful, the PCNGuard team can offer a reviewed 10-PCN extension or help you choose a paid plan.',
  },
] as const;

export function FleetMarketing() {
  usePageMeta({
    title: 'AI-First PCN Control for PCO Hire Companies - PCNGuard FleetMode',
    description: 'Check 3 PCNs free with PCNGuard FleetMode. AI extracts, checks, flags, and prepares PCN next actions for PCO hire teams.',
    canonical: 'https://pcnguard.co.uk/fleet',
    ogTitle: 'PCNGuard FleetMode - AI-first PCN control for PCO hire companies',
    ogDescription: 'Upload a PCN. Let AI extract, check, flag, and prepare the next action faster than manual admin.',
  });

  const trialUrl = getFleetTrialSignupUrl();
  const betaAnnualDiscount = '30% off first year';

  const trackTrialClick = (placement: string) => {
    Analytics.track('fleet_trial_cta_clicked', {
      placement,
      destination: trialUrl,
      audience: 'pco_hire',
      trial: '3pcn-ai-control',
      duration: '7d',
    });
  };

  const painPoints = [
    {
      title: 'PCNs arrive across vehicles and drivers',
      detail: 'Your team has to work out which PCO driver had the vehicle at the contravention time.',
      icon: Users,
    },
    {
      title: 'Discount windows leak value',
      detail: 'A late action can turn an ordinary notice into a larger avoidable cost.',
      icon: Clock,
    },
    {
      title: 'Evidence is scattered',
      detail: 'Hire agreements, liability clauses, driver details, and case notes often sit in different places.',
      icon: ClipboardCheck,
    },
    {
      title: 'Admin-fee recovery is hard to see',
      detail: 'Owners need to know what is recovered, pending, waived, disputed, or written off.',
      icon: PoundSterling,
    },
    {
      title: 'Transfer paperwork still needs preparing',
      detail: 'Even when the right driver is clear, teams still need a structured way to prepare liability transfer submissions and supporting records.',
      icon: FileText,
    },
  ] as const;

  const trialSteps = [
    {
      title: 'Check 3 PCNs free',
      detail: 'Create a FleetMode workspace. No card required.',
      icon: ShieldCheck,
    },
    {
      title: 'Upload 3 PCNs',
      detail: 'Use photos or PDFs from real PCO hire cases.',
      icon: UploadCloud,
    },
    {
      title: 'Review the AI outputs',
      detail: 'Check extracted fields, deadline risk, liability readiness, and evidence gaps.',
      icon: SearchCheck,
    },
    {
      title: 'Get your trial snapshot',
      detail: 'See estimated time saved, cost exposure, and recovery status from the sample.',
      icon: BarChart3,
    },
  ] as const;

  const aiSpeedProof = [
    {
      title: 'AI extracts PCN fields for review',
      detail: 'PCN number, VRM, dates, amount, issuer, location, and stage are pulled into a review-ready workflow.',
      icon: FileText,
    },
    {
      title: 'AI flags missing hire evidence',
      detail: 'FleetMode highlights missing agreement, driver, liability clause, or supporting record gaps before action.',
      icon: ClipboardCheck,
    },
    {
      title: 'AI highlights deadline risk',
      detail: 'Discount windows, representation deadlines, overdue stages, and escalation risk are made visible for operators.',
      icon: AlertTriangle,
    },
    {
      title: 'AI prepares the next action',
      detail: 'Your team gets a structured route for transfer, appeal, pay, recharge, or follow-up review.',
      icon: SearchCheck,
    },
  ] as const;

  const competitorContrast = [
    {
      label: 'Broad processing tools',
      title: 'Fleetworks-style workflow',
      detail: 'Broad fleet PCN reassignment and payment processing for teams that need to move fines through a general workflow.',
      bullets: ['Reassignment and payment motion', 'Broad issuer coverage messaging', 'General fleet operations focus'],
      highlight: false,
    },
    {
      label: 'AI-first PCO hire control',
      title: 'PCNGuard FleetMode',
      detail: 'AI-first PCN control for PCO hire companies that need faster review, driver matching, evidence readiness, and recovery visibility.',
      bullets: ['AI extraction and review', 'Driver and hire-period matching', 'Liability readiness and evidence gaps', 'Deadline and admin-fee recovery tracking'],
      highlight: true,
    },
  ] as const;

  const workflowModes = [
    {
      label: 'Current campaign focus',
      title: 'PCO hire PCN workflow',
      detail: 'For companies renting vehicles to private-hire drivers and handling repeat PCNs across drivers, hire periods, transfer records, and recovery.',
      bullets: [
        'Match PCNs to drivers and hire periods.',
        'Check liability readiness and evidence gaps.',
        'Prepare transfer-of-liability submission records.',
        'Track PCN cost and admin-fee recovery status.',
      ],
      highlight: true,
    },
    {
      label: 'Available module',
      title: 'Owner-managed fleet workflow',
      detail: 'For operators who own or manage vehicles and need to track, appeal, pay, or recharge PCNs internally.',
      bullets: [
        'Track PCNs on company-owned or managed vehicles.',
        'Use appeal assistance when the operator keeps responsibility.',
        'Choose pay, appeal, or internal recharge as the next action.',
        'Keep a clear case record for finance and operations.',
      ],
      highlight: false,
    },
  ] as const;

  const assistants = [
    {
      name: 'Intake Assistant',
      detail: 'Extracts PCN number, vehicle registration, dates, amounts, location, and issuer details for review.',
      icon: FileText,
    },
    {
      name: 'Liability Assistant',
      detail: 'Checks whether the case has enough driver and vehicle context to support a liability decision.',
      icon: Users,
    },
    {
      name: 'Evidence Assistant',
      detail: 'Flags missing hire agreement, liability clause, driver identity, or supporting records.',
      icon: ClipboardCheck,
    },
    {
      name: 'Transfer Submission Helper',
      detail: 'Organises the reviewed PCN details, driver match, hire evidence, and supporting notes for a transfer-of-liability submission workflow.',
      icon: FileText,
    },
    {
      name: 'Deadline Assistant',
      detail: 'Highlights discount, representation, overdue, and escalation risk so urgent cases are visible.',
      icon: AlertTriangle,
    },
    {
      name: 'Appeal, Pay & Recharge Assistant',
      detail: 'Helps prepare appeal drafts or internal action notes when the operator owns or manages the vehicle, then tracks pay or recharge status.',
      icon: PoundSterling,
    },
  ] as const;

  const snapshotMetrics = [
    ['PCNs processed', '3/3'],
    ['Estimated admin time saved', '72 min'],
    ['Deadline risk flagged', '1 case'],
    ['Recovery status checked', 'GBP 185'],
  ] as const;

  const pricingPlans = [
    {
      name: 'FleetMode Starter',
      fee: 'GBP 79/month',
      badge: betaAnnualDiscount,
      description: 'For a small PCO hire team proving a controlled PCN workflow.',
      features: [
        'Unlimited PCNs',
        '1 team seat',
        'AI-first PCN intake and review',
        'Per-PCN AI summary',
        'Basic deadline and liability readiness',
        'Email support',
      ],
      highlight: false,
    },
    {
      name: 'FleetMode Growth',
      fee: 'GBP 179/month',
      badge: betaAnnualDiscount,
      description: 'For active PCO hire companies that need team visibility and reporting.',
      features: [
        'Unlimited PCNs',
        '3 team seats',
        'AI extraction, evidence checks, and next-action support',
        'Monthly aggregate report',
        'Repeat driver and location flags',
        'Recovery rate tracking',
        'Hire agreement storage',
        'Priority support + onboarding call',
      ],
      highlight: true,
    },
    {
      name: 'FleetMode Pro Fleet',
      fee: 'GBP 299/month',
      badge: betaAnnualDiscount,
      description: 'For higher-volume operators that need stronger evidence and audit controls.',
      features: [
        'Unlimited PCNs',
        '10 team seats',
        'Everything in Growth',
        'Evidence pack generation',
        'Legal-ready PDF export',
        'Proof-of-submission log',
        'Audit trail',
        'Driver risk scoring',
        'Role-based access',
        'Bulk PCN actions',
      ],
      highlight: false,
    },
    {
      name: 'FleetMode Concierge',
      fee: 'Custom',
      badge: 'Scoped plan',
      description: 'For complex workflows, multiple branches, or hands-on support needs.',
      features: [
        'Unlimited team seats',
        'Everything in Pro Fleet',
        'Multi-branch controls',
        'API access (Phase 3)',
        'Dedicated account contact',
        'Managed onboarding',
        'Custom SLA',
      ],
      highlight: false,
    },
  ] as const;

  return (
    <div className="min-h-screen bg-[#fffaf1] text-[#081b1f] selection:bg-[#d9a45b]/25 font-sans">
      <Navbar />

      <main id="main-content">
        <section className="relative overflow-hidden border-b border-[#d7c3a8] bg-[#f4eadb] px-4 pb-12 pt-20 sm:px-6 sm:pb-16 sm:pt-24">
          <div className="max-w-7xl mx-auto relative">
            <div className="grid overflow-hidden border border-[#d7c3a8] bg-[#fffaf1] shadow-[0_34px_110px_rgba(8,27,31,0.16)] lg:grid-cols-[0.92fr_1.08fr]">
              <div className="relative px-5 py-8 sm:px-8 sm:py-10 md:px-10 md:py-12">
                <div className="max-w-3xl animate-[fadeInUp_0.65s_ease-out_both]">
                  <div className="inline-flex items-center gap-2 rounded-md border border-[#dcc8aa] bg-[#f6ead8] px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-[#8a5c22] sm:text-xs">
                    Free 3-PCN FleetMode Check
                  </div>

                  <h1 className="mt-5 font-space text-3xl font-black leading-[1.05] tracking-tight text-[#081b1f] sm:text-4xl md:text-5xl">
                    AI-first PCN control for PCO hire companies
                  </h1>

                  <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#596764] sm:text-lg">
                    Upload a PCN. Let AI extract, check, flag, and prepare the next action faster than manual admin while your team stays in control of every decision.
                  </p>

                  <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                    <a
                      href={trialUrl}
                      onClick={() => trackTrialClick('hero')}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#194d3a] px-6 py-3 font-bold text-white shadow-lg shadow-[#194d3a]/20 transition-all duration-200 hover:bg-[#26654e] active:translate-y-px sm:w-auto"
                    >
                      Check 3 PCNs free
                      <span aria-hidden="true">-&gt;</span>
                    </a>
                  </div>

                  <p className="mt-4 text-[11px] font-mono uppercase tracking-wider text-[#8a5c22] sm:text-xs">
                    7-day check. No card required. Ends after 3 PCNs processed or 7 days, whichever comes first.
                  </p>

                </div>

                <div className="mt-8 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
                  {[
                    ['Trial size', '3 PCNs'],
                    ['Trial length', '7 days'],
                    ['AI-first review', 'Included'],
                  ].map(([label, value], index) => (
                    <div
                      key={label}
                      className="min-h-[92px] rounded-lg border border-[#c7ddcf] bg-[#eaf3ee] px-4 py-3 shadow-sm animate-[fadeInUp_0.7s_ease-out_both]"
                      style={{ animationDelay: `${index * 0.06}s` }}
                    >
                      <div className="font-mono text-[11px] uppercase tracking-wider text-[#194d3a]">{label}</div>
                      <div className="mt-1 whitespace-nowrap font-space text-lg font-bold text-[#081b1f]">{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative min-h-[430px] border-t border-[#d7c3a8] bg-[#e8eef0] lg:border-l lg:border-t-0">
                <img
                  src={fleetHeroImage}
                  alt="FleetMode PCN workflow dashboard for PCO hire company operations"
                  className="absolute inset-0 h-full w-full object-cover object-[54%_center]"
                  loading="eager"
                  decoding="sync"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#081b1f]/10 via-transparent to-[#fffaf1]/20" />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#fffaf1] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 grid gap-6 lg:grid-cols-[0.78fr_1.22fr] lg:items-end">
              <div>
                <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                  AI speed proof
                </div>
                <h2 className="mt-5 font-space text-3xl font-bold text-[#081b1f] md:text-4xl">
                  Faster PCN review starts with AI doing the first pass.
                </h2>
              </div>
              <p className="text-base leading-7 text-[#596764] sm:text-lg">
                FleetMode is not only a ticket list. It is an AI-first control workflow that prepares the facts, flags risk, and gives your admin team a clearer next action.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {aiSpeedProof.map((proof) => (
                <article key={proof.title} className="rounded-lg border border-[#e4d8c8] bg-[#f6f0e6] p-5 shadow-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md border border-[#b5d5c4] bg-[#eaf3ee] text-[#194d3a]">
                    <proof.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-space text-lg font-bold text-[#081b1f]">{proof.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#596764]">{proof.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#081b1f] px-4 py-16 text-white sm:px-6 sm:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:items-start">
            <div>
              <div className="inline-flex rounded-md border border-white/15 px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#d9a45b]">
                PCO hire reality
              </div>
              <h2 className="mt-5 font-space text-3xl font-bold leading-tight tracking-tight sm:text-5xl">
                PCN admin is a profit-centre control problem.
              </h2>
              <p className="mt-5 text-base leading-7 text-[#c8d2cf]">
                PCO hire companies already have people handling fines. FleetMode is built to help those teams move faster, spot risk earlier, and keep a clearer recovery record.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {painPoints.map((point) => (
                <article key={point.title} className="border border-white/10 bg-white/[0.055] p-5 shadow-[0_18px_60px_rgba(0,0,0,0.18)]">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#d9a45b] text-[#081b1f]">
                    <point.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-space text-xl font-bold text-white">{point.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#c8d2cf]">{point.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#f6f0e6] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-3xl">
              <h2 className="font-space text-3xl font-bold text-[#081b1f] md:text-4xl">
                How the 3-PCN trial works
              </h2>
              <p className="mt-4 text-base leading-7 text-[#596764] sm:text-lg">
                Start with a small sample. First value should come from one processed PCN, not from a full migration project.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
              {trialSteps.map((step, index) => (
                <article key={step.title} className="border-t-4 border-[#194d3a] bg-[#fffaf1] p-5 shadow-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md border border-[#b5d5c4] bg-[#eaf3ee] text-[#194d3a]">
                    <step.icon className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-mono text-[11px] uppercase tracking-wider text-[#8a5c22]">Step {index + 1}</div>
                  <h3 className="mt-1 font-space text-lg font-bold text-[#081b1f]">{step.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-[#596764]">{step.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#fffaf1] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 grid gap-6 lg:grid-cols-[0.78fr_1.22fr] lg:items-end">
              <div>
                <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                  Two FleetMode workflows
                </div>
                <h2 className="mt-5 font-space text-3xl font-bold text-[#081b1f] md:text-4xl">
                  Built first for PCO hire, with an owner-managed fleet module.
                </h2>
              </div>
              <p className="text-base leading-7 text-[#596764] sm:text-lg">
                The website campaign stays focused on PCO hire companies, but the same FleetMode workspace also supports operators who own or manage vehicles and need appeal, payment, or internal recharge workflows.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {workflowModes.map((mode) => (
                <article
                  key={mode.title}
                  className={`rounded-lg border p-5 shadow-sm sm:p-6 ${mode.highlight ? 'border-[#194d3a] bg-[#eaf3ee]' : 'border-[#e4d8c8] bg-[#fffaf1]'}`}
                >
                  <div className={`inline-flex rounded-md border px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider ${mode.highlight ? 'border-[#b5d5c4] bg-[#194d3a] text-white' : 'border-[#dcc8aa] bg-[#f6f0e6] text-[#8a5c22]'}`}>
                    {mode.label}
                  </div>
                  <h3 className="mt-4 font-space text-2xl font-bold text-[#081b1f]">{mode.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#596764]">{mode.detail}</p>
                  <ul className="mt-5 grid gap-3">
                    {mode.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2.5 text-sm leading-6 text-[#344347]">
                        <CheckCircle className="mt-1 h-4 w-4 shrink-0 text-[#194d3a]" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#fffaf1] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-3xl">
              <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                Six review points
              </div>
              <h2 className="mt-5 font-space text-3xl font-bold text-[#081b1f] md:text-4xl">
                One PCN workflow, six review points
              </h2>
              <p className="mt-4 text-base leading-7 text-[#596764] sm:text-lg">
                FleetMode prepares the intake, liability, evidence, transfer submission, deadline, and appeal/pay/recharge views your team needs to review each PCN.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {assistants.map((assistant) => (
                <article key={assistant.name} className="rounded-lg border border-[#e4d8c8] bg-[#f6f0e6] p-5 shadow-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md border border-[#b5d5c4] bg-[#eaf3ee] text-[#194d3a]">
                    <assistant.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-space text-lg font-bold text-[#081b1f]">{assistant.name}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#596764]">{assistant.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#081b1f] px-4 py-14 text-white sm:px-6 sm:py-16">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.86fr_1.14fr] lg:items-center">
            <div>
              <div className="inline-flex rounded-md border border-white/15 px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#d9a45b]">
                Trial output
              </div>
              <h2 className="mt-5 font-space text-3xl font-bold leading-tight sm:text-4xl">
                Your 3-PCN AI Trial Snapshot
              </h2>
              <p className="mt-5 text-base leading-7 text-[#c8d2cf]">
                After 3 PCNs, FleetMode gives the operator a short snapshot of estimated admin time saved, deadline risk, driver-liability readiness, evidence gaps, and recovery status.
              </p>
            </div>

            <div className="border border-white/10 bg-white/[0.055] p-5 shadow-[0_24px_80px_rgba(0,0,0,0.22)] sm:p-6">
              <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-4">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-wider text-[#d9a45b]">Sample snapshot</div>
                  <h3 className="mt-1 font-space text-2xl font-bold text-white">North London PCO Hire Ltd</h3>
                </div>
                <div className="rounded-md bg-[#d9a45b] px-3 py-1 font-mono text-xs font-bold uppercase tracking-wider text-[#081b1f]">
                  Ready
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {snapshotMetrics.map(([label, value]) => (
                  <div key={label} className="border border-white/10 bg-[#f6efe3] p-4 text-[#081b1f]">
                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#8a5c22]">{label}</div>
                    <div className="mt-1 font-space text-2xl font-bold">{value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-5 grid gap-3">
                {[
                  ['Liability readiness', '2 cases look ready for driver follow-up; 1 needs missing hire evidence.'],
                  ['Transfer submission helper', 'Reviewed PCN details and evidence can be organised for the transfer-of-liability workflow.'],
                  ['Deadline risk', '1 case is near a discount deadline and should be reviewed first.'],
                  ['Recovery status', 'PCN cost and admin-fee recovery can be tracked as pending, recovered, waived, disputed, or written off.'],
                ].map(([label, detail]) => (
                  <div key={label} className="flex gap-3 border border-white/10 bg-white/[0.06] p-4">
                    <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#d9a45b]" />
                    <div>
                      <div className="font-space font-bold text-white">{label}</div>
                      <p className="mt-1 text-sm leading-6 text-[#c8d2cf]">{detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#fffaf1] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 grid gap-6 lg:grid-cols-[0.78fr_1.22fr] lg:items-end">
              <div>
                <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                  Direct comparison
                </div>
                <h2 className="mt-5 font-space text-3xl font-bold text-[#081b1f] md:text-4xl">
                  Broad processing is useful. AI-first control is the wedge for PCO hire.
                </h2>
              </div>
              <p className="text-base leading-7 text-[#596764] sm:text-lg">
                Fleetworks publicly leads with reassignment, payment processing, broad issuer coverage, and tiered fleet plans. PCNGuard FleetMode should win where PCO hire companies need faster review, evidence control, and recovery visibility before the next action is chosen.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {competitorContrast.map((item) => (
                <article
                  key={item.title}
                  className={`rounded-lg border p-5 shadow-sm sm:p-6 ${item.highlight ? 'border-[#194d3a] bg-[#eaf3ee]' : 'border-[#e4d8c8] bg-[#f6f0e6]'}`}
                >
                  <div className={`inline-flex rounded-md border px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider ${item.highlight ? 'border-[#b5d5c4] bg-[#194d3a] text-white' : 'border-[#dcc8aa] bg-[#fffaf1] text-[#8a5c22]'}`}>
                    {item.label}
                  </div>
                  <h3 className="mt-4 font-space text-2xl font-bold text-[#081b1f]">{item.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#596764]">{item.detail}</p>
                  <ul className="mt-5 grid gap-3">
                    {item.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2.5 text-sm leading-6 text-[#344347]">
                        <CheckCircle className="mt-1 h-4 w-4 shrink-0 text-[#194d3a]" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="pricing" className="bg-[#f6f0e6] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
              <div>
                <div className="inline-flex rounded-md border border-[#dcc8aa] bg-[#fffaf1] px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-[#8a5c22]">
                  Beta pricing preview
                </div>
                <h2 className="mt-5 font-space text-3xl font-bold text-[#081b1f] md:text-4xl">
                  Beta plans for PCO hire PCN control
                </h2>
              </div>
              <p className="text-base leading-7 text-[#596764] sm:text-lg">
                Unlimited PCNs. Choose by AI depth, team seats, reports, evidence controls, and support.
              </p>
            </div>

            <div className="mb-6 rounded-lg border border-[#d9a45b] bg-[#fffaf1] p-5 shadow-sm">
              <div className="font-mono text-[11px] uppercase tracking-wider text-[#8a5c22]">Beta annual lock-in</div>
              <p className="mt-2 max-w-4xl text-sm leading-6 text-[#344347]">
                Choose yearly during beta and lock in 30% off your first year. Monthly pricing remains available for teams that want to start smaller.
              </p>
            </div>

            <div className="mb-6 rounded-lg border border-[#b5d5c4] bg-[#eaf3ee] p-5 shadow-sm">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-wider text-[#194d3a]">ROI anchor</div>
                  <p className="mt-1 max-w-3xl text-sm leading-6 text-[#344347]">
                    A PCO hire company processing 60 PCNs/month with a GBP 20 admin fee has GBP 1,200/month moving through this workflow. FleetMode is positioned as infrastructure for controlling that process.
                  </p>
                </div>
                <Gauge className="h-10 w-10 shrink-0 text-[#194d3a]" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {pricingPlans.map((plan) => (
                <article
                  key={plan.name}
                  className={`flex min-h-[520px] flex-col rounded-lg border p-5 shadow-sm ${plan.highlight ? 'border-[#194d3a] bg-[#194d3a] text-white shadow-[#194d3a]/20' : 'border-[#e4d8c8] bg-[#fffaf1] text-[#081b1f]'}`}
                >
                  <div className={`font-mono text-[11px] uppercase tracking-wider ${plan.highlight ? 'text-[#d9f0e4]' : 'text-[#8a5c22]'}`}>
                    {plan.name}
                  </div>
                  <div className={`mt-3 inline-flex rounded-md border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${plan.highlight ? 'border-white/25 bg-white/10 text-white' : 'border-[#b5d5c4] bg-[#eaf3ee] text-[#194d3a]'}`}>
                    {plan.badge}
                  </div>
                  <div className={`mt-2 font-space text-2xl font-bold ${plan.highlight ? 'text-white' : 'text-[#081b1f]'}`}>{plan.fee}</div>
                  <p className={`mt-3 text-sm leading-6 ${plan.highlight ? 'text-[#d9f0e4]' : 'text-[#596764]'}`}>{plan.description}</p>
                  <ul className="mt-5 flex-1 space-y-2">
                    {plan.features.map((feature) => (
                      <li key={feature} className={`flex items-start gap-2 text-sm ${plan.highlight ? 'text-[#d9f0e4]' : 'text-[#596764]'}`}>
                        <CheckCircle className={`mt-0.5 h-4 w-4 shrink-0 ${plan.highlight ? 'text-white' : 'text-[#194d3a]'}`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#fffaf1] px-4 py-14 sm:px-6 sm:py-16" aria-label="Frequently asked questions">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-3xl">
              <h2 className="font-space text-3xl font-bold text-[#081b1f] md:text-4xl">
                PCO hire trial questions
              </h2>
              <p className="mt-4 text-base leading-7 text-[#596764] sm:text-lg">
                Clear answers for operators who already have an admin process and want to test FleetMode without a long setup.
              </p>
            </div>
            <div className="space-y-3">
              {FLEET_FAQS.map((faq) => (
                <details key={faq.q} className="group overflow-hidden rounded-lg border border-[#e4d8c8] bg-[#f6f0e6] shadow-sm">
                  <summary className="flex cursor-pointer list-none items-center justify-between p-4 text-base font-bold text-[#081b1f] [&::-webkit-details-marker]:hidden sm:p-6 sm:text-lg">
                    {faq.q}
                    <span className="ml-4 shrink-0 text-[#8a5c22] transition-all duration-300 group-open:rotate-45 group-open:text-[#194d3a]">+</span>
                  </summary>
                  <div className="px-4 pb-4 leading-relaxed text-[#596764] sm:px-6 sm:pb-6">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#f6f0e6] px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto grid max-w-5xl grid-cols-1 items-stretch gap-5 lg:grid-cols-[0.92fr_1.08fr]">
            <div className="rounded-lg border border-[#e4d8c8] bg-[#fffaf1] p-5 shadow-sm sm:p-6">
              <div className="mb-4 flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-[#194d3a]" />
                <h3 className="font-space text-xl font-bold text-[#081b1f]">Built to prove value fast</h3>
              </div>
              <p className="text-sm leading-6 text-[#596764] sm:text-base">
                Upload 3 real PCNs and see FleetMode extract the facts, flag evidence gaps, highlight deadline risk, and prepare the next action. Your team can judge the AI workflow on live cases before moving more of the operation across. If you want a bigger sample, ask for the reviewed 10-PCN extension after the first snapshot.
              </p>
            </div>

            <div className="rounded-lg border border-[#b5d5c4] bg-[#eaf3ee] p-5 shadow-sm sm:p-6">
              <h3 className="font-space text-2xl font-bold text-[#081b1f]">Ready to test 3 PCNs?</h3>
              <p className="mt-3 text-sm leading-6 text-[#596764] sm:text-base">
                Create your workspace and upload three notices from your PCO hire workflow. No card required.
              </p>
              <div className="mt-5 grid gap-3 sm:grid-cols-[minmax(220px,1.35fr)_minmax(104px,0.55fr)_minmax(148px,0.8fr)]">
                <a
                  href={trialUrl}
                  onClick={() => trackTrialClick('final_cta')}
                  className="inline-flex min-h-12 items-center justify-center gap-3 whitespace-nowrap rounded-md bg-[#194d3a] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-[#194d3a]/25 transition-all duration-200 hover:bg-[#26654e] active:translate-y-px sm:text-base"
                >
                  Check 3 PCNs free
                  <span aria-hidden="true">-&gt;</span>
                </a>
                <a
                  href={getAppLoginUrl()}
                  className="inline-flex min-h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#d6c7b6] bg-white px-4 py-3 text-sm font-bold text-[#344347] transition-all duration-200 hover:border-[#194d3a] hover:bg-[#fffaf1] hover:text-[#194d3a] sm:text-base"
                >
                  Sign in
                </a>
                <a
                  href="/fleet/features"
                  className="inline-flex min-h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#d6c7b6] bg-white px-4 py-3 text-sm font-bold text-[#344347] transition-all duration-200 hover:border-[#194d3a] hover:bg-[#fffaf1] hover:text-[#194d3a] sm:text-base"
                >
                  Explore features
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <style>{`
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(18px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default FleetMarketing;
